import type { Article } from '@/types'

import { img } from '@/constants/media'

export const articles: Article[] = [
    {
        slug: 'fewer-projects-more-attention',
        category: '(Studio Notes)',
        title: 'Fewer projects, more attention.',
        date: '6/15/26',
        datetime: '2026-06-15',
        image: img('journal-fewer-projects.jpg'),
        aspect: 1.2,
        excerpt: 'Taking on half the work was the only way to double what each piece of it was worth.',
        body: [
            'We used to run six projects at once. It looked like growth. It was mostly context switching, and the work showed it: good enough, on time, and forgettable.',
            'So we cut the roster in half and doubled the attention on what was left. Nothing about the process changed. What changed was how much of it each project actually got.',
            'The founder is on every call. The person who wrote the strategy is the person choosing the typeface. There is no brief being relayed down a chain, losing a little of itself at each step.',
            'Two clients a month is not a scarcity tactic. It is the number where we can still argue about kerning at 6pm on a Thursday and mean it.'
        ]
    },
    {
        slug: 'awards-dont-build-brands',
        category: '(Opinion)',
        title: "Awards don't build brands.",
        date: '5/5/26',
        datetime: '2026-05-05',
        image: img('journal-awards.jpg'),
        aspect: 0.8,
        excerpt: 'A shelf of trophies is a lovely thing. It has never once closed a round.',
        body: [
            'We have won a few. They are on a shelf. Not one of them has appeared in a single conversation with a client about whether the work moved the business.',
            'Awards reward the thing a jury can see in nine seconds. Brands are built in the part nobody photographs: the tenth page nobody talks about, the empty state, the invoice.',
            'The useful question is not whether the work is award-worthy. It is whether someone who has never heard of you understands what you do and why it matters, in the time it takes to scroll one screen.',
            'Do that, and the awards tend to follow anyway. Chase them first and you get a beautiful site that nobody can use.'
        ]
    },
    {
        slug: 'why-we-quote-fixed',
        category: '(Process)',
        title: 'Why we quote fixed.',
        date: '4/3/26',
        datetime: '2026-04-03',
        image: img('journal-quote-fixed.jpg'),
        aspect: 1.0,
        excerpt: 'Hourly billing makes every good idea feel expensive. Fixed scope makes them free.',
        body: [
            'The moment a client is paying by the hour, every suggestion becomes a negotiation. Should we try a second direction? That depends on what it costs. Should we redraw the logo? Maybe next time.',
            'Fixed scope kills that conversation. We agree what we are making and what it costs before day one, and then the only thing left to talk about is whether the work is good.',
            'It puts the risk on us, which is where it belongs. If something takes longer than we thought, that is a failure of our estimate, not a surprise on your invoice.',
            'Scope is the only thing that moves the number, and it moves it on the first call, in daylight, before anyone has started.'
        ]
    },
    {
        slug: 'the-best-type-is-invisible',
        category: '(Craft)',
        title: 'The best type is invisible.',
        date: '3/20/26',
        datetime: '2026-03-20',
        image: img('journal-type.jpg'),
        aspect: 0.85,
        excerpt: 'If someone compliments your typeface before your message, something has gone wrong.',
        body: [
            'Typography is not decoration. It is the speed at which someone can take in what you are saying, and everything else is downstream of that.',
            'A measure that is too wide costs you a reader every paragraph. Line height set by eye rather than by rhythm makes a page feel heavy without anyone knowing why.',
            'We argue about kerning because the argument is really about clarity. Nobody notices good spacing. Everybody feels bad spacing, even if they never name it.',
            'The goal is not a typeface people remember. It is a sentence they finish.'
        ]
    },
    {
        slug: 'on-saying-no',
        category: '(Studio Notes)',
        title: 'On saying no.',
        date: '2/18/26',
        datetime: '2026-02-18',
        image: img('journal-saying-no.jpg'),
        aspect: 1.15,
        excerpt: 'The fastest way to protect a studio is to turn down the work that would have drained it.',
        body: [
            'Not every brief is a fit, and pretending otherwise helps nobody. A project where design is not the real problem will not be fixed by better design.',
            'So we say no early, on the first call, while it still costs nothing. Usually we can point somewhere better: a positioning consultant, a developer, occasionally just a clearer conversation with their own team.',
            'Saying no is not precious. It is the only way the yes means anything, because it is what keeps the calendar honest.',
            'The clients we do take on get a studio that is not quietly resenting three other projects.'
        ]
    },
    {
        slug: 'the-brief-before-the-brief',
        category: '(Process)',
        title: 'The brief before the brief.',
        date: '1/28/26',
        datetime: '2026-01-28',
        image: img('journal-brief.jpg'),
        aspect: 0.78,
        excerpt: 'Most briefs describe a deliverable. The useful one describes a problem.',
        body: [
            'A brief that opens with "we need a new website" has already skipped the interesting part. A website is an answer. We want the question.',
            'So the first call is not about pages or sections. It is about who you are losing to, what they have that you do not, and what someone believes about you before they ever hear you speak.',
            'That conversation usually rewrites the brief. Sometimes it turns a site project into a brand project. Once or twice it has turned a brand project into a two-page note and no invoice.',
            'Get the question right and the deliverable almost designs itself.'
        ]
    },
    {
        slug: 'small-looks-expensive',
        category: '(Craft)',
        title: 'Small looks expensive.',
        date: '12/12/25',
        datetime: '2025-12-12',
        image: img('journal-small.jpg'),
        aspect: 1.05,
        excerpt: 'Restraint reads as confidence. Volume reads as someone hoping something lands.',
        body: [
            'Luxury brands are not quiet by accident. Saying one thing, once, at the right size, signals that you already know it is enough.',
            'Every extra badge, banner and call to action is a small admission of doubt. Put five of them on a page and the page starts to plead.',
            'The discipline is subtraction. Take out the second headline. Take out the third button. Take out the stock photo that is there because the column looked empty.',
            'What is left has room to be looked at, and being looked at is most of the job.'
        ]
    },
    {
        slug: 'the-founder-is-the-brand',
        category: '(Opinion)',
        title: 'The founder is the brand.',
        date: '11/4/25',
        datetime: '2025-11-04',
        image: img('journal-founder.jpg'),
        aspect: 0.75,
        excerpt: 'For the first few years, people are not buying the company. They are buying you.',
        body: [
            'Early on there is no track record to lean on, no category position, no thirty-year story. There is a person with a point of view and a willingness to say it out loud.',
            'That is not a weakness to design around. It is the most defensible thing a young company has, because nobody can copy it.',
            'So the identity should sound like someone rather than something. The writing should have a temperature. The photography should include a face.',
            'The company can grow past the founder later. It cannot start without them.'
        ]
    }
]

export const getArticle = (slug: string): Article | undefined => {
    return articles.find((article) => article.slug === slug)
}
